<template>
    <div :class="{ 'ppv2-pointer-events-none ppv2-opacity-50': disabled }">
        <!-- Error Messages -->
        <div v-if="inputsErrors && inputsErrors.length > 0" class="ppv2-space-y-2 ppv2-mb-4">
            <InfoMessageBox
                v-for="(error, index) in inputsErrors"
                :key="index"
                :message="error.message"
                color="danger"
                class="ppv2-mb-2"
            />
        </div>
        <div
            v-if="isLoading"
            class="ppv2-text-center"
            :style="{ paddingTop: normalizeSizeVertical(32, true), paddingBottom: normalizeSizeVertical(32, true) }"
        >
            <div
                class="ppv2-inline-block ppv2-animate-spin ppv2-rounded-full ppv2-h-8 ppv2-w-8 ppv2-border-b-2 ppv2-border-gray-900"
            ></div>
            <p class="ppv2-mt-2">{{ $t('resource_planning.loading') }}</p>
        </div>
        <div
            v-else-if="!localDays || localDays.length === 0"
            class="ppv2-text-center ppv2-text-gray-500"
            :style="{ paddingTop: normalizeSizeVertical(32, true), paddingBottom: normalizeSizeVertical(32, true) }"
        >
            {{ $t('resource_planning.no_schedule') }}
        </div>
        <div v-else>
            <div
                v-for="(day, idx) in localDays"
                :key="'day-' + idx + '-' + (day.name || '')"
                class="ppv2-mb-6"
                :hidden="day.hashed"
            >
                <div class="ppv2-flex ppv2-flex-col md:ppv2-flex-row md:ppv2-items-center md:ppv2-justify-between">
                    <div v-if="inputType === 'switch'" class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-w-56">
                        <BaseSwitch v-model="day.enabled" :id="'switch-' + idx" />
                        <!-- Label -->
                        <label :for="'switch-' + idx" class="poppinsBold ppv2-text-xs ppv2-text-black ppv2-cursor-pointer">
                            {{ $t('resource_planning.week_days.' + day.name.toLowerCase()) }}
                        </label>
                    </div>
                    <div v-if="inputType === 'checked'" class="ppv2-flex ppv2-flex-col ppv2-items-start ppv2-gap-2 ppv2-w-56">
                        <!-- Label -->
                        <label :for="'switch-' + idx" class="poppinsBold ppv2-text-xs ppv2-text-black ppv2-cursor-pointer">
                            {{ $t('resource_planning.week_days.' + day.name.toLowerCase()) }}
                        </label>
                        <div class="ppv2-flex ppv2-items-center ppv2-space-x-2">
                            <BaseCheckbox
                                v-model="day.close"
                                label=""
                                :id="'checkbox-' + idx"
                                @input="onCheckboxChange($event, day)"
                            />
                            <label
                                :for="'checkbox-' + idx"
                                class="poppinsRegular ppv2-text-xs ppv2-text-black ppv2-cursor-pointer"
                                >{{ $t('common.close') }}</label
                            >
                        </div>
                    </div>
                    <div class="ppv2-w-full">
                        <div class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-mt-2 md:ppv2-mt-0 ppv2-w-full">
                            <div class="ppv2-flex ppv2-items-center ppv2-gap-12 ppv2-w-full">
                                <template v-if="day.periods && day.periods[0]">
                                    <div class="ppv2-flex-1">
                                        <BaseTimeInput
                                            :id="`input-${idx}-0-start`"
                                            v-model="day.periods[0].start"
                                            :placeholder="'09:00'"
                                            input-class="!ppv2-border-[#5B5B9C]"
                                            :customSizeLabel="12"
                                            :label="$t('resource_planning.exceptions.exception_types.open2')"
                                            inputClass="!ppv2-text-xs"
                                        />
                                    </div>

                                    <div class="ppv2-flex-1">
                                        <BaseTimeInput
                                            :id="`input-${idx}-0-end`"
                                            v-model="day.periods[0].end"
                                            :placeholder="'18:00'"
                                            input-class="!ppv2-border-[#5B5B9C]"
                                            :customSizeLabel="12"
                                            inputClass="!ppv2-text-xs"
                                            :label="$t('resource_planning.exceptions.exception_types.close')"
                                        />
                                    </div>
                                </template>
                                <span v-else>-</span>
                            </div>
                            <div class="ppv2-w-32 ppv2-ml-2 space-x-1">
                                <button
                                    :disabled="day.lunches.length >= 1"
                                    class="copy-button ppv2-text-black ppv2-text-opacity-90 hover:ppv2-text-opacity-100 ppv2-mt-[12px]"
                                    @click="addBreak(idx)"
                                >
                                    <CopyPlus :size="17" />
                                </button>
                                <button
                                    class="copy-button ppv2-text-black ppv2-text-opacity-90 hover:ppv2-text-opacity-100 ppv2-mt-[12px]"
                                    @click="copyDay(idx)"
                                >
                                    <Copy :size="17" />
                                </button>
                            </div>
                        </div>
                        <!-- Lunch Breaks -->
                        <div v-for="(lunch, lunchIdx) in day.lunches" :key="'lunch-' + idx + '-' + lunchIdx" class="ppv2-mt-1">
                            <div class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-w-full">
                                <div class="ppv2-flex ppv2-items-center ppv2-gap-12 ppv2-w-full">
                                    <div class="ppv2-flex-1">
                                        <BaseTimeInput
                                            :id="`lunch-${idx}-${lunchIdx}-start`"
                                            v-model="lunch.start"
                                            :placeholder="'12:00'"
                                            input-class="!ppv2-border-[#5B5B9C]"
                                            :customSizeLabel="12"
                                            :label="$t('resource_planning.break')"
                                            inputClass="!ppv2-text-xs"
                                        />
                                    </div>

                                    <div class="ppv2-flex-1">
                                        <BaseTimeInput
                                            :id="`lunch-${idx}-${lunchIdx}-end`"
                                            v-model="lunch.end"
                                            :placeholder="'13:30'"
                                            input-class="!ppv2-border-[#5B5B9C]"
                                            :customSizeLabel="12"
                                            :label="$t('resource_planning.break')"
                                            inputClass="!ppv2-text-xs"
                                        />
                                    </div>
                                </div>
                                <div class="ppv2-w-32">
                                    <button
                                        :hidden="true"
                                        class="copy-button ppv2-text-black ppv2-text-opacity-90 hover:ppv2-text-opacity-100 ppv2-mt-[12px]"
                                        @click="addBreak(idx)"
                                    >
                                        <Plus :size="17" />
                                    </button>
                                    <button
                                        class="delete-button ppv2-ml-2"
                                        @click="removeBreak(idx, lunchIdx)"
                                        :title="$t('resource_planning.remove_break')"
                                    >
                                        <Trash2 :size="17" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import BaseTimeInput from '../../Shared/BaseTimeInput.vue'
import BaseSwitch from '../../Shared/BaseSwitch.vue'
import BaseCheckbox from '../../Shared/BaseCheckbox.vue'
import InfoMessageBox from '../../Shared/InfoMessageBox.vue'
import { Copy, CopyPlus, Trash2, Plus } from 'lucide-vue'
import normalizeSizeVertical from '../../../utils/normalizeSizeVertical'

export default {
    name: 'DayPlanning',
    components: {
        BaseTimeInput,
        BaseSwitch,
        BaseCheckbox,
        InfoMessageBox,
        Copy,
        CopyPlus,
        Trash2,
        Plus,
    },
    emits: ['update:days'],
    props: {
        days: {
            type: Array,
            default: () => [],
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        isExperience: {
            type: Boolean,
            default: false,
        },
        showAll: {
            Boolean,
            default: true,
        },
        disabled: {
            Boolean,
            default: false,
        },
        inputType: {
            type: String,
            default: 'switch', // could be "checked" or "switch"
        },
    },
    data() {
        return {
            localDays: [],
            inputsErrors: [],
        }
    },
    mounted() {
        this.verifyInputs()
    },
    created() {
        // console.log('DayPlanning created with days:', this.days)
        // The watcher will handle the initial setup
        this.localDays = this.getDefaultDays() // Initialize with default values

        // If we have initial days, process them
        if (this.days && Array.isArray(this.days) && this.days.length > 0) {
            console.log(' created localDays', this.days)
            this.localDays = this.processDays(this.days)
        }
    },
    watch: {
        days: {
            immediate: true,
            handler(newVal) {
                console.log('watch days')
                if (newVal && Array.isArray(newVal) && newVal.length > 0) {
                    // Only update if the new value is different from current local state
                    const newDays = newVal.map((day) => ({
                        ...day,
                        periods:
                            Array.isArray(day.periods) && day.periods.length > 0
                                ? [...day.periods]
                                : [{ start: '09:00', end: '18:00' }],
                        lunches: Array.isArray(day.lunches) ? [...day.lunches] : [],
                    }))

                    // Only update if the data has actually changed
                    if (JSON.stringify(this.localDays) !== JSON.stringify(newDays)) {
                        this.localDays = newDays
                        // console.log('Updated localDays from prop:', this.localDays)
                    }
                }
            },
            deep: true,
        },
        localDays: {
            handler(newVal) {
                console.log('watch localDay')
                this.verifyInputs()
                this.$emit('update:days', JSON.parse(JSON.stringify(newVal)), this.inputsErrors.length == 0)
            },
            immediate: true,
            deep: true,
        },
    },
    methods: {
        normalizeSizeVertical,
        verifyInputs() {
            const errors = []

            this.localDays.forEach((day, dayIndex) => {
                // Skip validation for closed days.
                // "enabled=false" (switch mode) or "close=true" (checkbox mode) means the day is off;
                // its time inputs are typically 00:00/00:00 and must not trigger an invalid range error.
                if (day.enabled === false || day.close === true) {
                    return
                }
                // Check if day has periods
                if (day.periods && day.periods.length > 0) {
                    day.periods.forEach((period, periodIndex) => {
                        // Check if period has both start and end times
                        if (!period.start || !period.end) {
                            errors.push({
                                dayIndex,
                                field: 'period',
                                message: this.$t('resource_planning.errors.missing_time', { day: day.label }),
                                periodIndex,
                            })
                        } else {
                            if (period.start && period.end) {
                                const [startHours, startMinutes] = period.start.split(':').map(Number)
                                const [endHours, endMinutes] = period.end.split(':').map(Number)

                                // Check if start time is before end time
                                if (startHours > endHours || (startHours === endHours && startMinutes >= endMinutes)) {
                                    errors.push({
                                        dayIndex,
                                        field: 'period',
                                        message: this.$t('resource_planning.errors.invalid_time_range', { day: day.label }),
                                        periodIndex,
                                    })
                                }
                            }
                        }
                    })
                }

                // Check lunch breaks
                if (day.lunches && day.lunches.length > 0) {
                    day.lunches.forEach((lunch, lunchIndex) => {
                        // Skip validation when both are 00:00 (no lunch break)
                        if (lunch.start === '00:00' && lunch.end === '00:00') return
                        // Check if lunch has both start and end times
                        if (lunch.start && lunch.end) {
                            const [startHours, startMinutes] = lunch.start.split(':').map(Number)
                            const [endHours, endMinutes] = lunch.end.split(':').map(Number)

                            // Check if lunch start is before lunch end
                            if (startHours > endHours || (startHours === endHours && startMinutes > endMinutes)) {
                                errors.push({
                                    dayIndex,
                                    field: 'lunch',
                                    message: this.$t('resource_planning.errors.invalid_lunch_range', { day: day.label }),
                                    lunchIndex,
                                })
                            }

                            // Check if lunch is within working hours
                            if (day.periods && day.periods[0] && day.periods[0].start && day.periods[0].end) {
                                const workStart = day.periods[0].start
                                const workEnd = day.periods[0].end

                                if (lunch.start < workStart || lunch.end > workEnd) {
                                    errors.push({
                                        dayIndex,
                                        field: 'lunch',
                                        message: this.$t('resource_planning.errors.lunch_outside_work_hours', { day: day.label }),
                                        lunchIndex,
                                    })
                                }
                            }
                        }
                    })
                }
            })

            this.inputsErrors = errors
            // return errors;
        },
        processDays(days) {
            if (!Array.isArray(days)) return []
            // console.log(' processDays days', days)
            return days.map((day) => ({
                day: day.day,
                name: day.name || '',
                label: day.label || '',
                enabled: day.enabled !== undefined ? day.enabled : true,
                periods:
                    Array.isArray(day.periods) && day.periods.length > 0
                        ? day.periods.map((period) => ({
                              start: period.start || '09:00',
                              end: period.end || '18:00',
                          }))
                        : [{ start: '09:00', end: '18:00' }],
                lunches: Array.isArray(day.lunches)
                    ? day.lunches.map((lunch) => ({
                          start: lunch.start || '12:00',
                          end: lunch.end || '13:30',
                      }))
                    : [],
                hashed: this.showAll === false && Array.isArray(day.periods) && day.periods.length == 0,
                // hashed:false
            }))
        },
        getDefaultDays() {
            return [
                {
                    name: 'monday',
                    day: 1,
                    label: this.$t('resource_planning.week_days.monday'),
                    enabled: true,
                    periods: [{ start: '07:00', end: '09:00' }],
                    lunches: [],
                },
                {
                    name: 'tuesday',
                    day: 2,
                    label: this.$t('resource_planning.week_days.tuesday'),
                    enabled: true,
                    periods: [{ start: '07:00', end: '09:00' }],
                    lunches: [],
                },
                {
                    name: 'wednesday',
                    day: 3,
                    label: this.$t('resource_planning.week_days.wednesday'),
                    enabled: true,
                    periods: [{ start: '07:00', end: '09:00' }],
                    lunches: [],
                },
                {
                    name: 'thursday',
                    day: 4,
                    label: this.$t('resource_planning.week_days.thursday'),
                    enabled: true,
                    periods: [{ start: '07:00', end: '09:00' }],
                    lunches: [],
                },
                {
                    name: 'friday',
                    day: 5,
                    label: this.$t('resource_planning.week_days.friday'),
                    enabled: true,
                    periods: [{ start: '07:00', end: '09:00' }],
                    lunches: [],
                },
                {
                    name: 'saturday',
                    day: 6,
                    label: this.$t('resource_planning.week_days.saturday'),
                    enabled: true,
                    periods: [{ start: '07:00', end: '09:00' }],
                    lunches: [],
                },
                {
                    name: 'sunday',
                    day: 0,
                    label: this.$t('resource_planning.week_days.sunday'),
                    enabled: true,
                    periods: [{ start: '07:00', end: '09:00' }],
                    lunches: [],
                },
            ]
        },
        addBreak(dayIndex) {
            if (this.localDays[dayIndex]) {
                if (!this.localDays[dayIndex].lunches) {
                    this.$set(this.localDays[dayIndex], 'lunches', [])
                }
                this.localDays[dayIndex].lunches.push({
                    start: '12:00',
                    end: '13:30',
                })
                // Update the local state and emit changes
                this.updateDay(dayIndex)
            }
        },
        removeBreak(dayIndex, lunchIndex) {
            if (this.localDays[dayIndex].lunches && this.localDays[dayIndex].lunches[lunchIndex]) {
                this.localDays[dayIndex].lunches.splice(lunchIndex, 1)
                // Update the local state and emit changes
                this.updateDay(dayIndex)
            }
        },
        updateDay(dayIndex) {
            // Create a new object to trigger reactivity
            const updatedDay = { ...this.localDays[dayIndex] }

            // Update the local state
            this.$set(this.localDays, dayIndex, updatedDay)

            // Emit the changes to the parent
            this.$emit('update:days', [...this.localDays])
        },
        copyDay(idx) {
            if (idx < this.localDays.length - 1) {
                const nextDay = this.localDays[idx + 1]
                nextDay.enabled = this.localDays[idx].enabled
                nextDay.periods = JSON.parse(JSON.stringify(this.localDays[idx].periods))
                nextDay.lunches = JSON.parse(JSON.stringify(this.localDays[idx].lunches))
                this.$forceUpdate()
            }
        },
        focusTimeInput(dayIdx, periodIdx, field) {
            // Compose ref name
            const refName = `input-${dayIdx}-${periodIdx}-${field}`
            this.$nextTick(() => {
                const input = this.$refs[refName]
                const target = Array.isArray(input) ? input[0] : input
                if (target) {
                    target.focus()
                    // Use click to open the native picker reliably
                    if (typeof target.click === 'function') {
                        target.click()
                    }
                }
            })
        },
        onCheckboxChange(value, day) {
            console.log(value)
            day.enabled = !value
        },
    },
}
</script>

<style scoped>
.copy-button {
    transition: color 0.2s ease-in-out;
}

.delete-button {
    color: #de2222e6;
    transition: color 0.2s ease-in-out;
}
.dark .delete-button {
    color: rgba(255, 156, 156, 0.9);
}

.delete-button:hover {
    color: #ff9c9c;
}
</style>
